import React from 'react';
import { ShieldCheck, X } from 'lucide-react';

/**
 * Big prominent OTP display shown to staff (or visitors) after a gate event.
 *
 * The same component covers all flows:
 *   • Check-in / Check-out
 *   • Break out / Break in
 *   • Visit out / Visit in
 *   • Visitor entry / exit
 *
 * Props:
 *   • otp        — string e.g. "00042" (already zero-padded by the server)
 *   • label      — short heading e.g. "Check-in"
 *   • subtitle   — optional one-liner under the heading
 *   • autoApproved — when true, server reports gate-control is OFF for this
 *                    staff's geofence → instruct them to write in the register
 *   • onClose
 */
export default function GateOtpModal({ otp, label, subtitle, autoApproved, onClose }) {
  if (!otp) return null;
  return (
    <div className="fixed inset-0 bg-black/80 z-[200] flex items-center justify-center p-4" data-testid="gate-otp-modal" onClick={onClose}>
      <div
        className="bg-white rounded-3xl max-w-sm w-full overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gradient-to-br from-indigo-600 to-blue-700 text-white px-6 py-5 relative">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-1 hover:bg-white/20 rounded-full"
            data-testid="gate-otp-close"
            aria-label="Close"
          >
            <X size={18} />
          </button>
          <div className="flex items-center gap-2 mb-1 text-white/90 text-xs uppercase tracking-wider font-semibold">
            <ShieldCheck size={14} /> {label || 'Gate'} OTP
          </div>
          {subtitle && <p className="text-white/80 text-sm">{subtitle}</p>}
        </div>

        <div className="px-4 py-6 text-center" data-testid="gate-otp-number-container">
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Your number</p>
          <p
            className="font-bold text-indigo-700 tracking-[0.15em] tabular-nums select-all whitespace-nowrap"
            style={{ fontSize: 'clamp(2.25rem, 13vw, 3.25rem)', lineHeight: '1.1' }}
            data-testid="gate-otp-number"
          >
            {otp}
          </p>

          <div className={`mt-5 rounded-xl px-4 py-3 text-sm ${autoApproved ? 'bg-amber-50 text-amber-800 border border-amber-200' : 'bg-emerald-50 text-emerald-800 border border-emerald-200'}`}>
            {autoApproved ? (
              <>📝 Gate security is <strong>OFF</strong> for your office.<br />Please <strong>write this number</strong> in the gate register.</>
            ) : (
              <>🛡️ Show this number to the <strong>gate security</strong> to be let through.</>
            )}
          </div>
        </div>

        <button
          onClick={onClose}
          className="w-full py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium border-t"
          data-testid="gate-otp-dismiss"
        >
          Got it
        </button>
      </div>
    </div>
  );
}
